import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import api from './api';

const KATEGORI_ETIKETLER = {
  yukseltme_talebi: { label: 'Hesap Yükseltme', renk: 'warning' },
  teknik_sorun: { label: 'Teknik Sorun', renk: 'danger' },
  fatura: { label: 'Fatura', renk: 'info' },
  diger: { label: 'Diğer', renk: 'secondary' },
};

const DURUM_ETIKETLER = {
  acik: { label: 'Açık', renk: 'success' },
  beklemede: { label: 'Beklemede', renk: 'warning' },
  cozuldu: { label: 'Çözüldü', renk: 'primary' },
  reddedildi: { label: 'Reddedildi', renk: 'danger' },
};

function AdminDestekPanel() {
  const navigate = useNavigate();
  const [ticketlar, setTicketlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);

  // Filtreler
  const [durumFiltre, setDurumFiltre] = useState('');
  const [kategoriFiltre, setKategoriFiltre] = useState('');

  // SSE — yeni mesaj geldiğinde liste güncellenir
  useEffect(() => {
    const token = window._accessToken || localStorage.getItem('accessToken');
    if (!token) return;

    const url = `${process.env.REACT_APP_API_URL}/admin/destek/stream`;
    let aktif = true;

    const fetchSSE = async () => {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!response.ok) return;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const read = async () => {
          while (aktif) {
            try {
              const { value, done } = await reader.read();
              if (done) break;
              const text = decoder.decode(value, { stream: true });
              const lines = text.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  // Yeni mesaj geldi → liste güncelleme tarihleri değişti
                  try {
                    const mesaj = JSON.parse(line.slice(6));
                    // Ilgili ticket'ın guncelleme_tarihi ve mesaj_sayisini güncelle
                    setTicketlar(prev =>
                      prev.map(t =>
                        t.id === mesaj.ticket_id
                          ? { ...t, guncelleme_tarihi: mesaj.tarih, mesaj_sayisi: (t.mesaj_sayisi || 0) + 1 }
                          : t
                      )
                    );
                  } catch { /* JSON parse hatası */ }
                }
              }
            } catch {
              break;
            }
          }
          // Bağlantı koptu, 3 saniye sonra yeniden dene
          if (aktif) setTimeout(fetchSSE, 3000);
        };
        read();
      } catch {
        if (aktif) setTimeout(fetchSSE, 5000);
      }
    };

    fetchSSE();
    return () => { aktif = false; };
  }, []);

  const fetchTicketlar = useCallback(async () => {
    setYukleniyor(true);
    try {
      const params = new URLSearchParams();
      if (durumFiltre) params.append('durum', durumFiltre);
      if (kategoriFiltre) params.append('kategori', kategoriFiltre);
      const res = await api.get(`/admin/destek/ticketlar?${params.toString()}`);
      setTicketlar(res.data);
    } catch {
      setHata('Ticket\'lar yüklenirken hata oluştu.');
    } finally {
      setYukleniyor(false);
    }
  }, [durumFiltre, kategoriFiltre]);

  useEffect(() => {
    fetchTicketlar();
  }, [fetchTicketlar]);

  const acikSayisi = ticketlar.filter(t => ['acik', 'beklemede'].includes(t.durum)).length;

  return (
    <Container fluid className="py-4 px-3 px-md-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="fw-bold mb-0">
            <span className="material-symbols-outlined align-middle me-2" style={{ fontSize: '1.6rem' }}>support_agent</span>
            Destek Talepleri
          </h4>
          <p className="text-muted small mt-1 mb-0">
            {acikSayisi > 0 ? (
              <><Badge bg="danger" pill className="me-1">{acikSayisi}</Badge> açık talep bekliyor</>
            ) : 'Bekleyen açık talep yok'}
          </p>
        </Col>
      </Row>

      {/* Filtreler */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-2">
            <Col xs={12} sm={6} md={4}>
              <Form.Select
                value={durumFiltre}
                onChange={e => setDurumFiltre(e.target.value)}
                size="sm"
              >
                <option value="">Tüm Durumlar</option>
                {Object.entries(DURUM_ETIKETLER).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Select
                value={kategoriFiltre}
                onChange={e => setKategoriFiltre(e.target.value)}
                size="sm"
              >
                <option value="">Tüm Kategoriler</option>
                {Object.entries(KATEGORI_ETIKETLER).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm="auto">
              <Button variant="outline-secondary" size="sm" onClick={fetchTicketlar}>
                <span className="material-symbols-outlined align-middle" style={{ fontSize: '1rem' }}>refresh</span>
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Ticket listesi */}
      {yukleniyor ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : hata ? (
        <Alert variant="danger">{hata}</Alert>
      ) : ticketlar.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <span className="material-symbols-outlined d-block text-muted mb-3" style={{ fontSize: '3rem' }}>inbox</span>
            <p className="text-muted mb-0">Filtre kriterlerine uygun talep bulunamadı.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <div className="list-group list-group-flush">
              {ticketlar.map(ticket => {
                const kat = KATEGORI_ETIKETLER[ticket.kategori] || KATEGORI_ETIKETLER.diger;
                const dur = DURUM_ETIKETLER[ticket.durum] || DURUM_ETIKETLER.acik;
                const acik = ['acik', 'beklemede'].includes(ticket.durum);
                return (
                  <button
                    key={ticket.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 px-4 py-3 ${acik ? 'border-start border-4 border-warning' : ''}`}
                    onClick={() => navigate(`/admin/destek/ticket/${ticket.id}`)}
                  >
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{ticket.baslik}</div>
                      <div className="small text-muted">
                        <strong>{ticket.kullanici_adi}</strong>
                        {' · '}
                        {new Date(ticket.guncelleme_tarihi).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                        {' · '}
                        {ticket.mesaj_sayisi} mesaj
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <Badge bg={kat.renk} className="text-dark">{kat.label}</Badge>
                      <Badge bg={dur.renk}>{dur.label}</Badge>
                    </div>
                    <span className="material-symbols-outlined text-muted">chevron_right</span>
                  </button>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default AdminDestekPanel;
